import { Injectable } from '@angular/core';
import { APIAssetService, APIQuoteScheduleService, APIUserService } from '@takectrl-multiapp/svcs-ctrl-api';
import {
  AssetType,
  IAlertBrokerOfEndorsement,
  IAPIQuoteConvertResponse,
  IBank,
  IBankBranches,
  IBankingDetails,
  IBuilding,
  ICasaDetails,
  IContent,
  IEndorsementUpdateAsset,
  IInsurersDetails,
  InputType,
  IPremiaBankingDetails,
  IPremiaLOV,
  IQuestion,
  IQuote,
  IQuoteConvertResponse,
  IQuoteErrorRepsonse,
  IQuoteReject,
  IQuoteRejectReason,
  IQuoteRequest,
  IResponse,
  IResponseMotorVehiclesSnakeCase,
  ISchedule,
  ISpecialConditionAssets,
  QuestionDependencyType,
  QuestionType,
  QuoteRequestStatusType,
  QuoteRequestType,
  SharedConfig,
  SharedFormulaService,
  UnderwritingAnswer,
} from '@takectrl-multiapp/svcs-share-config';
import validate from 'validate-id-za';
import { BUSNAssetService } from './busn-asset.service';

import { AlertController } from '@ionic/angular';
import { BUSNThirdPartyService } from '@takectrl-multiapp/svcs-core-business';
import { ICtrlResponse } from 'libs/svcs-ctrl-api/src/lib/interfaces/api.interface';
import { SharedConfigService } from 'libs/svcs-share-config/src/lib/services/shared-config.service';
import { firstValueFrom, take } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class BUSNQuoteScheduleService {
  errorDescription: string;
  constructor(
    private readonly busnAssetServiceService: BUSNAssetService,
    public sharedConfig: SharedConfig,
    private readonly alertController: AlertController,
    private readonly apiQuoteScheduleService: APIQuoteScheduleService,
    private readonly busnThirdPartyService: BUSNThirdPartyService,
    private readonly sharedFormulaService: SharedFormulaService,
    private readonly apiUserService: APIUserService,
    private readonly apiAssetService: APIAssetService,
    public sharedConfigService: SharedConfigService
  ) {}

  async getAllActiveSchedules(): Promise<Array<ISchedule>> {
    let schedulesAll = (await firstValueFrom(this.apiQuoteScheduleService.getAllSchedules())).results;
    console.log(schedulesAll);
    let activeSchedulesWithAssets: any;
    if (!Array.isArray(schedulesAll) || schedulesAll.length === 0) {
      activeSchedulesWithAssets = [];
    } else {
      activeSchedulesWithAssets = schedulesAll.filter((x) => x.status === 'ACTIVE' || x.status === 'PENDING_ENDORSE');
    }
    return activeSchedulesWithAssets;
  }

  // @Josh, add the type in here
  async checkForUpdatedPremiums(): Promise<ICtrlResponse<unknown>> {
    return await firstValueFrom(this.apiQuoteScheduleService.premiumCheck());
  }

  async getQuoteErrorMessage(quote_request_id: number, insurer_id: string): Promise<ICtrlResponse<IQuoteErrorRepsonse | {}>> {
    return await firstValueFrom(this.apiQuoteScheduleService.getQuoteErrorMessage(quote_request_id, insurer_id));
  }

  async getDeclineQuoteReasons() {
    return (await firstValueFrom(this.apiQuoteScheduleService.getDeclineQuoteReasons())).results as Array<IQuoteRejectReason>;
  }

  async rejectQuote(payload: IQuoteReject) {
    return (await firstValueFrom(this.apiQuoteScheduleService.rejectQuote(payload))).success as boolean;
  }

  async getQuoteSchedule(quote_id: number) {
    return (await firstValueFrom(this.apiQuoteScheduleService.getQuoteSchedule(quote_id))) as Blob
  }

  async getPremiaPolicySchedule(schedule_id: number) {
    return (await firstValueFrom(this.apiQuoteScheduleService.getPremiaPolicySchedule(schedule_id))) as Blob
  }

  async submitQuoteRequest(): Promise<IResponse> {
    this.busnThirdPartyService.logEvent('action_quote_requested', { action: 'requested' });
    SharedConfig.acknowledgedQuoteRequestError = false;

    // Request of https://ctrltech.atlassian.net/browse/CT-6440 iterate through assets and do address lookup and set/update quoterequest.

    try {
      this.sharedConfig.quoteRequest.needs_analysis_completed = new Date().getTime();
      this.sharedConfig.quoteRequest.status = QuoteRequestStatusType.Submitted;
      await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

      let quoteCreateResponse: ICtrlResponse<any> = await firstValueFrom(
        this.apiQuoteScheduleService.initiateQuoteRequestProcessing(this.sharedConfig.quoteRequest.id)
      );
      if (quoteCreateResponse.success === true) {
        return {
          success: true,
        };
      } else {
        // completely failed
        this.sharedConfig.quoteRequest.status = QuoteRequestStatusType.Failed;
        this.updateQuoteRequest(this.sharedConfig.quoteRequest);
        this.busnThirdPartyService.logEvent('business_quote_request_failed', {
          uid: SharedConfig.firebaseUserKey,
          name: this.sharedConfig.user.personal_details.name,
          email: this.sharedConfig.user.personal_details.email,
          timestamp: new Date().getTime(),
          quote_request_id: this.sharedConfig.quoteRequest.id,
        });
        return {
          success: false,
        };
      }
    } catch (error) {
      return {
        message: error.message,
        success: false,
      };
    }
  }

  async submitEndorsementRequest(): Promise<IResponse> {
    this.sharedConfig.setShowCustomLoader(true, 'submitEndorsementRequest');
    if (this.sharedConfig.quoteRequest.type === QuoteRequestType.EndorsementAdd) {
      this.busnThirdPartyService.logEvent('action_endorsement_add_submitted');
    } else {
      this.busnThirdPartyService.logEvent('action_endorsement_update_submitted');
    }
    SharedConfig.acknowledgedQuoteRequestError = false;
    try {
      this.sharedConfig.quoteRequest.status = QuoteRequestStatusType.Submitted;
      await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

      let quoteCreateResponse = await firstValueFrom(
        this.apiQuoteScheduleService.initiateEndorsementRequestProcessing(this.sharedConfig.quoteRequest.id)
      );

      if (quoteCreateResponse.success) {
        this.sharedConfig.setShowCustomLoader(false, 'submitEndorsementRequest');
        return {
          success: true,
        };
      } else {
        this.busnThirdPartyService.logEvent('business_endorsement_request_failed', {
          uid: SharedConfig.firebaseUserKey,
          name: this.sharedConfig.user.personal_details.name,
          email: this.sharedConfig.user.personal_details.email,
          timestamp: new Date().getTime(),
          quote_request_id: this.sharedConfig.quoteRequest.id,
        });
        this.sharedConfig.setShowCustomLoader(false, 'submitEndorsementRequest');
        return {
          success: false,
          message: "Something went wrong, we'll contact you shortly",
        };
      }
    } catch (error) {
      this.sharedConfig.setShowCustomLoader(false, 'submitEndorsementRequest');
      return {
        message: error.message,
        success: false,
      };
    }
  }

  async endorsementUpdateAlertBroker(alertBrokerPayload: IAlertBrokerOfEndorsement): Promise<ICtrlResponse<IAlertBrokerOfEndorsement>> {
    return await firstValueFrom(this.apiQuoteScheduleService.endorsementUpdateAlertBroker(alertBrokerPayload));
  }

  async getQuoteRequestById(userId: number, quote_id: number): Promise<IQuoteRequest> {
    if (!userId) console.warn('getQuoteRequestById(userId: string, quote_id: string): Promise<DatabaseSnapshot> call will fail as userId missing');
    return (await firstValueFrom(this.apiQuoteScheduleService.getQuoteRequestById(quote_id))).results;
  }

  async getQuoteRequests(): Promise<Array<IQuoteRequest>> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getQuoteRequests())).results;
  }

  async getQuoteRequestsCurrent(): Promise<Array<IQuoteRequest>> {
    const allUserQuoteRequests = (await firstValueFrom(this.apiQuoteScheduleService.getQuoteRequests())).results;
    const currentQuoteRequest = allUserQuoteRequests && allUserQuoteRequests.length > 0 ? allUserQuoteRequests?.filter(
      (quote_request: IQuoteRequest) =>
        quote_request.status === 'FAILED' ||
        quote_request.status === 'OPEN' ||
        quote_request.status === 'PENDING' ||
        quote_request.status === 'SUBMITTED' ||
        quote_request.status === 'WAITING_ON_INSURER' ||
        quote_request.status === 'WAITING_ON_QUOTES' ||
        quote_request.status === 'WAITING_TO_ACCEPT'
    ) : [];
    return currentQuoteRequest;
  }

  async underwriting_questions_completed(quoteRequest: IQuoteRequest): Promise<void> {
    await firstValueFrom(this.apiQuoteScheduleService.setUnderwritingQuestionsCompleted(quoteRequest));
  }

  async optionals_completed(quoteRequest: IQuoteRequest): Promise<void> {
    await firstValueFrom(this.apiQuoteScheduleService.setOptionalsCompleted(quoteRequest));
  }

  async confirmation_completed(quoteRequest: IQuoteRequest): Promise<void> {
    await firstValueFrom(this.apiQuoteScheduleService.setConfirmationCompleted(quoteRequest));
  }

  async saveQuoteQuestions(questions: Array<IQuestion>, asset: any) {
    let policyHolder = false;
    if (questions[0]?.underwriting_answers[0]?.object_name)
      switch (questions[0].underwriting_answers[0].object_name) {
        case AssetType.AllRisks:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.AllRisks
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    SharedConfig.selectedAssetAllRiskItem[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              SharedConfig.selectedAssetAllRiskItem[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }

          await this.busnAssetServiceService.updateAllRiskItem(SharedConfig.selectedAssetAllRiskItem);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.Buildings:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Buildings
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    SharedConfig.selectedAssetBuilding[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }

            for (const underwritingAnswer of question.underwriting_answers) {
              SharedConfig.selectedAssetBuilding[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }
          await this.busnAssetServiceService.updateBuilding(SharedConfig.selectedAssetBuilding);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.Caravans:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Caravans
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    SharedConfig.selectedAssetCaravan[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              SharedConfig.selectedAssetCaravan[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }
          await this.busnAssetServiceService.updateCaravan(SharedConfig.selectedAssetCaravan);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.Contents:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Contents
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    SharedConfig.selectedAssetContent[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              SharedConfig.selectedAssetContent[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }
          await this.busnAssetServiceService.updateContent(SharedConfig.selectedAssetContent);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.Motorcycles:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Motorcycles
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    SharedConfig.selectedAssetMotorcycle[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              SharedConfig.selectedAssetMotorcycle[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }
          await this.busnAssetServiceService.updateMotorcycle(SharedConfig.selectedAssetMotorcycle);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.SpecifiedItems:
          const specifiedItemsAssetIndex = this.sharedConfig.quoteRequest.assets.specified_items.findIndex((x) => x.id === asset.id);
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.SpecifiedItems
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    this.sharedConfig.quoteRequest.assets.specified_items[specifiedItemsAssetIndex][underwritingAnswer.object_field] = emptyValue;
                    SharedConfig.selectedAssetSpecifiedItem[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              this.sharedConfig.quoteRequest.assets.specified_items[specifiedItemsAssetIndex][underwritingAnswer.object_field] =
                underwritingAnswer.object_answer;
              SharedConfig.selectedAssetSpecifiedItem[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }
          }
          await this.busnAssetServiceService.updateSpecifiedItem(SharedConfig.selectedAssetSpecifiedItem);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        case AssetType.Vehicles:
          const QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.vehicles.findIndex((x) => x.id === asset.id);
          let tempExtraPersonal = [];
          let tempExtraInureanceHistory = [];
          for (const question of questions) {
            // This sets the  QuestionDependencyType.child object_answer to null and does the PATCH to backend. (API will return the child if you have answered it already and does not delete it.)
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Vehicles
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    this.sharedConfig.quoteRequest.assets.vehicles[QuoteAssetIndex][underwritingAnswer.object_field] = emptyValue;
                    SharedConfig.selectedAssetVehicle[underwritingAnswer.object_field] = emptyValue;
                  }
                }
              }
            }
            // THIS NEEDS TO BE ALLOWED TO BE NULL/NONE
            if (question.question_type === 'LIST') {
              if (!question.underwriting_answers[0].object_answer) {
                question.underwriting_answers[0].object_answer = [];
              }
            }
            for (const underwritingAnswer of question.underwriting_answers) {
              if (underwritingAnswer.object_answer === 'Policyholder') {
                policyHolder = true;
              }

              this.sharedConfig.quoteRequest.assets.vehicles[QuoteAssetIndex][underwritingAnswer.object_field] = underwritingAnswer.object_answer;
              SharedConfig.selectedAssetVehicle[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
            }

            if (policyHolder && question.person_checker?.source && question.person_checker?.source.indexOf('personal_details') > -1) {
              for (let underwritingAnswer of question.underwriting_answers) {
                if (underwritingAnswer.object_answer !== null) {
                  tempExtraPersonal.push(question);
                }
              }
            }

            if (policyHolder && question.person_checker?.source && question.person_checker?.source.indexOf('insurance_history') > -1) {
              for (let underwritingAnswer of question.underwriting_answers) {
                if (underwritingAnswer.object_answer !== null) {
                  tempExtraInureanceHistory.push(question);
                }
              }
            }
          }
          // this is throwing a too many requests error
          if (tempExtraPersonal.length > 0) {
            await this.updateUserDetailsAnswers(tempExtraPersonal);
          }

          if (tempExtraInureanceHistory.length > 0) {
            await this.updateUserInsuranceHistories(tempExtraInureanceHistory);
          }
          policyHolder = false;
          await this.busnAssetServiceService.updateVehicle(SharedConfig.selectedAssetVehicle);
          // this.updateQuoteRequest(this.sharedConfig.quoteRequest);
          break;

        //users.personal_details and users.insurance_history
        default:
          for (const question of questions) {
            if (question.dependency && question.dependency.dependency_type === QuestionDependencyType.parent) {
              const childDependencyQuestions = SharedConfig.underwritingQuestionsAll.filter(
                (x) =>
                  x.dependency &&
                  x.dependency.dependency_key === question.dependency.dependency_key &&
                  x.dependency.dependency_type === QuestionDependencyType.child &&
                  x.underwriting_answers[0].object_name === AssetType.Buildings
              );
              if (childDependencyQuestions && childDependencyQuestions.length > 0 && question.underwriting_answers[0].object_answer === false) {
                for (const childQuestion of childDependencyQuestions) {
                  const emptyValue = childQuestion.question_type === QuestionType.list ? [] : null;
                  for (const underwritingAnswer of childQuestion.underwriting_answers) {
                    if (question.underwriting_answers[0].object_name === AssetType.UserPersonalDetails) {
                      SharedConfig.user.personal_details[underwritingAnswer.object_field] = emptyValue;
                    } else {
                      SharedConfig.user.insurance_history[underwritingAnswer.object_field] = emptyValue;
                    }
                  }
                }
              }
            }

            for (const underwritingAnswer of question.underwriting_answers) { 
              if (question.underwriting_answers[0].object_name === AssetType.UserPersonalDetails) {
                SharedConfig.user.personal_details[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
              } else {
                if (SharedConfig.user.insurance_history) {
                  SharedConfig.user.insurance_history[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
                }
              }
            }
          }

          // Deep clone variables to not have them overridden by "getAllUserData"
          const insuranceHistoryToSave = { ...SharedConfig.user.insurance_history };
          const personalHistoryToSave = { ...SharedConfig.user.personal_details };

          // await firstValueFrom(this.apiUserService.updateUser(SharedConfig.user).pipe(take(1)));
          await firstValueFrom(this.apiUserService.updatePersonalDetails(personalHistoryToSave).pipe(take(1)));
          await firstValueFrom(this.apiUserService.updateUserInsuranceHistory(insuranceHistoryToSave).pipe(take(1)));

          break;
      }
  }

  async updateUserInsuranceHistories(questions: Array<IQuestion>) {
    if (!this.sharedConfig.user.insurance_history) this.sharedConfig.user.insurance_history = {};
    for (const question of questions) {
      for (const underwritingAnswer of question.underwriting_answers) {
        this.sharedConfig.user.insurance_history[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
      }
    }

    await firstValueFrom(this.apiUserService.updateUserInsuranceHistory(this.sharedConfig.user.insurance_history));
  }

  async updateUserDetailsAnswers(questions: Array<IQuestion>) {
    for (const question of questions) {
      for (const underwritingAnswer of question.underwriting_answers) {
        this.sharedConfig.user.personal_details[underwritingAnswer.object_field] = underwritingAnswer.object_answer;
      }
    }
    await firstValueFrom(this.apiUserService.updatePersonalDetails(this.sharedConfig.user.personal_details));
  }

  // async removeUnderwritingAnswers(underwriting_answers: UnderwritingAnswer[]) {
  //   for (const underwritingAnswer of underwriting_answers) {
  //     await this.removeAnswerFromQuoteRequestAndAsset(underwritingAnswer);
  //   }
  // }

  async removeAnswerFromQuoteRequestAndAsset(underwritingAnswer: UnderwritingAnswer, questionType: QuestionType) {
    let QuoteAssetIndex = -1;
    switch (underwritingAnswer.object_name) {
      case AssetType.Buildings:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.buildings.findIndex((x) => x.id === SharedConfig.selectedAssetBuilding.id);
        this.sharedConfig.quoteRequest.assets.buildings[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetBuilding[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateBuilding(SharedConfig.selectedAssetBuilding);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);
        break;

      case AssetType.Caravans:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.caravans.findIndex((x) => x.id === SharedConfig.selectedAssetCaravan.id);
        this.sharedConfig.quoteRequest.assets.caravans[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetCaravan[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateCaravan(SharedConfig.selectedAssetCaravan);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;

      case AssetType.Contents:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.contents.findIndex((x) => x.id === SharedConfig.selectedAssetContent.id);
        this.sharedConfig.quoteRequest.assets.contents[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetContent[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateContent(SharedConfig.selectedAssetContent);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;

      case AssetType.Motorcycles:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.motorcycles.findIndex((x) => x.id === SharedConfig.selectedAssetMotorcycle.id);
        this.sharedConfig.quoteRequest.assets.motorcycles[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetMotorcycle[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateMotorcycle(SharedConfig.selectedAssetMotorcycle);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;

      case AssetType.SpecifiedItems:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.specified_items.findIndex((x) => x.id === SharedConfig.selectedAssetSpecifiedItem.id);
        this.sharedConfig.quoteRequest.assets.specified_items[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetSpecifiedItem[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateSpecifiedItem(SharedConfig.selectedAssetSpecifiedItem);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;

      case AssetType.Vehicles:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.vehicles.findIndex((x) => x.id === SharedConfig.selectedAssetVehicle.id);
        this.sharedConfig.quoteRequest.assets.vehicles[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetVehicle[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateVehicle(SharedConfig.selectedAssetVehicle);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;

      case AssetType.AllRisks:
        QuoteAssetIndex = this.sharedConfig.quoteRequest.assets.all_risks.findIndex((x) => x.id === SharedConfig.selectedAssetAllRiskItem.id);
        this.sharedConfig.quoteRequest.assets.all_risks[QuoteAssetIndex][underwritingAnswer.object_field] =
          questionType === QuestionType.list ? [] : null;
        SharedConfig.selectedAssetAllRiskItem[underwritingAnswer.object_field] = questionType === QuestionType.list ? [] : null;
        await this.busnAssetServiceService.updateAllRiskItem(SharedConfig.selectedAssetAllRiskItem);
        // await this.updateQuoteRequest(this.sharedConfig.quoteRequest);

        break;
      default:
        break;
    }
  }

  regexValidator(answer, regexToValidateAgainst) {
    const regex = new RegExp(regexToValidateAgainst);
    return regex.test(answer);
  }

  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  // REFACTOR THIS -
  isFieldValidationSuccess(
    underwriting_answers: Array<UnderwritingAnswer>,
    question: IQuestion,
    quoteRequestSelectedAsset: any,
    content_building: IContent | IBuilding = null
  ): boolean {
    const questionType = question.question_type;

    for (const underwritingAnswer of underwriting_answers) {
      if (
        underwritingAnswer.object_answer !== null &&
        underwritingAnswer.object_answer !== undefined &&
        underwritingAnswer.object_answer.length !== 0
      ) {
        switch (questionType) {
          case QuestionType.boolean:
            if (typeof underwritingAnswer.object_answer !== 'boolean') {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide valid answer';
              return false;
            }
            break;
          case QuestionType.select:
            if (typeof underwritingAnswer.object_answer !== 'string' || question.options.indexOf(underwritingAnswer.object_answer) < 0) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide valid answer';
              return false;
            }
            break;
          case QuestionType.selectSearch:
            if (typeof underwritingAnswer.object_answer !== 'string') {
              this.errorDescription = 'Please provide valid answer';
              return false;
            }
            break;
          case QuestionType.multiSelect:
            if (!content_building) {
              if (question.underwriting_answers.find((x) => (x.object_answer === undefined && x.required) || typeof x.object_answer !== 'boolean')) {
                this.errorDescription = underwritingAnswer.object_answer_error = 'Please make a selection';
                return false;
              }
            }
            break;
          case QuestionType.text:
            if (underwritingAnswer.min && underwritingAnswer.object_answer.toString().length < underwritingAnswer.min) {
              this.errorDescription = underwritingAnswer.object_answer_error = `The value you entered must be longer than ${underwritingAnswer.min}`;
              return false;
            }

            if (underwritingAnswer.max && underwritingAnswer.object_answer.toString().length > underwritingAnswer.max) {
              this.errorDescription = underwritingAnswer.object_answer_error = `The value you entered must be shorter than ${underwritingAnswer.max}`;
              return false;
            }

            if (underwritingAnswer.regex) {
              if (!this.regexValidator(underwritingAnswer.object_answer, underwritingAnswer.regex)) {
                this.errorDescription = underwritingAnswer.object_answer_error = 'The value you entered is in the incorrect format';
                return false;
              }
            }

            if (typeof underwritingAnswer.object_answer !== 'string') {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide valid answer';
              return false;
            }

            if (underwritingAnswer.required && (!underwritingAnswer.object_answer || underwritingAnswer.object_answer.toString().length === 0)) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide an answer';
              return false;
            }
            if (
              (underwritingAnswer.object_field === 'id_nr' ||
                underwritingAnswer.object_field === 'regular_driver_id' ||
                underwritingAnswer.object_field === 'registered_owner_id') &&
              !validate(underwritingAnswer.object_answer)
            ) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'ID number is invalid';
              return false;
            }
            if (
              underwritingAnswer.object_field === 'id_nr' ||
              underwritingAnswer.object_field === 'regular_driver_id' ||
              underwritingAnswer.object_field === 'registered_owner_id'
            ) {
              const id = underwritingAnswer.object_answer;
              const y1o = id.substring(0, 1);
              const y2e = id.substring(1, 2);
              const m1o = id.substring(2, 3);
              const m2e = id.substring(3, 4);
              const d1o = id.substring(4, 5);
              const d2e = id.substring(5, 6);

              let dateInID = '';
              if (y1o === '0' && y2e === '0') {
                dateInID = `2000/${m1o}${m2e}/${d1o}${d2e}`;
              } else {
                dateInID = `${y1o + y2e}/${m1o}${m2e}/${d1o}${d2e}`;
              }
              const date_parse = Date.parse(dateInID);
              const testDateInID = isNaN(date_parse);
              if (testDateInID) {
                this.errorDescription = underwritingAnswer.object_answer_error = 'ID number is invalid';
                return false;
              }
            }
            break;
          case QuestionType.multi:
            const _numberAwnser = Number(underwritingAnswer.object_answer);
            if (underwritingAnswer.object_answer === undefined || Number.isNaN(underwritingAnswer.object_answer)) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide an answer';
              return false;
            }
            if (underwritingAnswer.max && _numberAwnser > underwritingAnswer.max) {
              this.errorDescription =
                underwritingAnswer.object_answer_error = `The answer for question (${underwritingAnswer.place_holder}) can't be more than ${underwritingAnswer.max}`;
              return false;
            }
            if (underwritingAnswer.min && _numberAwnser < underwritingAnswer.min) {
              this.errorDescription =
                underwritingAnswer.object_answer_error = `The answer for question (${underwritingAnswer.place_holder}) can't be less than ${underwritingAnswer.min}`;
              return false;
            }
            const chech_for_negative_numbers = Math.sign(_numberAwnser);
            if (chech_for_negative_numbers === -1) {
              this.errorDescription = underwritingAnswer.object_answer_error =
                'Please provide a valid number (no decimal or negative numbers allowed)';
              return false;
            }
            break;
          case QuestionType.number:
            const _numAnswer = Number(underwritingAnswer.object_answer);
            underwritingAnswer.object_answer = _numAnswer;
            const isnum = /^\d+$/.test(underwritingAnswer.object_answer);
            if (underwritingAnswer.object_answer === undefined || typeof underwritingAnswer.object_answer !== InputType.number) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide an answer';
              return false;
            }
            if (underwritingAnswer.min && _numAnswer < underwritingAnswer.min) {
              this.errorDescription = underwritingAnswer.object_answer_error = `The value you entered must be greater than ${underwritingAnswer.min}`;
              return false;
            }

            if (underwritingAnswer.max && _numAnswer > underwritingAnswer.max) {
              this.errorDescription = underwritingAnswer.object_answer_error = `The value you entered must be less than ${underwritingAnswer.max}`;
              return false;
            }

            if (!isnum) {
              this.errorDescription = underwritingAnswer.object_answer_error =
                'Please provide a valid number (no decimal or negative numbers allowed)';
              return false;
            }
            break;
          case QuestionType.vehicleSearch:
            const vehicle_type = question.underwriting_answers.find((x) => x.object_field === "vehicle_type");
            const year_of_registration = question.underwriting_answers.find((x) => x.object_field === "year_of_registration");
            const year_of_manufacture = question.underwriting_answers.find((x) => x.object_field === "year_of_manufacture");
            const make = question.underwriting_answers.find((x) => x.object_field === "make");
            const model = question.underwriting_answers.find((x) => x.object_field === "model");

            if (
              typeof year_of_manufacture.object_answer !== InputType.number ||
              typeof year_of_registration.object_answer !== InputType.number ||
              typeof vehicle_type.object_answer !== 'string' ||
              typeof model.object_answer !== 'string' ||
              typeof make.object_answer !== 'string' ||
              !year_of_manufacture.object_answer ||
              !year_of_registration.object_answer ||
              !model.object_answer ||
              !model.object_answer ||
              !make.object_answer
            ) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide an answer';
              return false;
            }
            break;
          default:
            break;
        }

        for (let index = 0; index < question.underwriting_answers.length; index++) {
          const element = question.underwriting_answers[index];
          if (element.object_field === 'days_work_from_home') {
            const chech_for_negative_numbers = Math.sign(element.object_answer);
            if (element.object_answer > element.max) {
              this.errorDescription = underwritingAnswer.object_answer_error = 'Exceeding max number of days in a week';
              return false;
            }
            if (chech_for_negative_numbers === -1) {
              this.errorDescription = underwritingAnswer.object_answer_error =
                'Please provide a valid number (no decimal or negative numbers allowed)';
              return false;
            }
          } else if (element.object_field === 'monthly_mileage') {
            if (element.object_answer > element.max) {
              this.errorDescription = `Kilometers per month can't be more than ${element.max}`;
              return false;
            }
          }
        }

        if (underwritingAnswer.object_field === 'sum_insured') {
          if (underwritingAnswer.object_answer < underwritingAnswer.min || underwritingAnswer.object_answer > underwritingAnswer.max) {
            this.errorDescription =
              underwritingAnswer.object_answer_error = `The answer must be more than ${underwritingAnswer.min} and less than ${underwritingAnswer.max}`;
            return false;
          }
        }

        if (
          underwritingAnswer.object_field === 'name' ||
          underwritingAnswer.object_field === 'surname' ||
          underwritingAnswer.object_field === 'surname_on_id'
        ) {
          const match = underwritingAnswer.object_answer.match(/\d+/g);
          if (match != null) {
            this.errorDescription = underwritingAnswer.object_answer_error = 'No numbers allowed';
            return false;
          }
        }

        if (
          underwritingAnswer.object_field === 'id_nr' ||
          underwritingAnswer.object_field === 'regular_driver_id' ||
          underwritingAnswer.object_field === 'registered_owner_id'
        ) {
          if (underwritingAnswer.object_answer.toString().length !== 13) {
            this.errorDescription = underwritingAnswer.object_answer_error = 'ID number requires 13 digits and only numbers are allowed';
            return false;
          }
        }

        if (underwritingAnswer.object_field === 'regular_driver_id') {
          SharedConfig.regularDriverIdNumber = underwritingAnswer.object_answer;
        }

        if (underwritingAnswer.object_field === 'regular_driver_license_type') {
          SharedConfig.regular_driver_license_type = underwritingAnswer.object_answer;
        }
        if (underwritingAnswer.object_field === 'regular_driver_license_date') {
          if (SharedConfig.regularDriverIdNumber) {
            const ageOnDriversLicense = this.sharedFormulaService.getAgeOnDriversLicense(
              underwritingAnswer.object_answer,
              SharedConfig.regularDriverIdNumber
            );
            // let ageOnDriversLicense = this.sharedFormulaService.getAgeFromIdNumber(SharedConfig.regularDriverIdNumber)
            if (SharedConfig.regular_driver_license_type === 'A1 - Motor Cycle < 125cc') {
              if (ageOnDriversLicense < 16) {
                this.errorDescription = underwritingAnswer.object_answer_error = 'You must be 16 and older to possess a code A1 license';
                return false;
              }
            } else if (SharedConfig.regular_driver_license_type === 'L - Learners') {
              if (ageOnDriversLicense < 17) {
                this.errorDescription = underwritingAnswer.object_answer_error = "You must be 17 and older to possess a Learner's license";
                return false;
              }
            } else {
              if (ageOnDriversLicense < 18) {
                this.errorDescription = underwritingAnswer.object_answer_error = "You must be 18 and older to possess a driver's license";
                return false;
              }
            }
          }
        }
      } else {
        if (underwritingAnswer.required) {
          if (questionType === QuestionType.vehicleSearch) {
            for (const _underwritingAnswer of underwriting_answers) {
              delete _underwritingAnswer.object_answer;
            }
          }
          this.errorDescription = underwritingAnswer.object_answer_error = 'Please provide an answer';
          return false;
        } else {
          return true;
        }
      }
    }
    this.errorDescription = null;
    return true;
  }

  dateIsValidyyyymmdd(dateStr: string) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;

    if (dateStr.match(regex) === null) {
      return false;
    }

    const date = new Date(dateStr);

    const timestamp = date.getTime();

    if (typeof timestamp !== InputType.number || Number.isNaN(timestamp)) {
      return false;
    }

    return date.toISOString().startsWith(dateStr);
  }

  async createNewQuoteRequest(showHideLoader = true): Promise<IQuoteRequest> {
    if (showHideLoader) {
      this.sharedConfig.setShowCustomLoader(true, 'createNewQuoteRequest');
    }
    const quoteRequestType: QuoteRequestType = QuoteRequestType.Quote;

    try {
      const quote_create_payload = {
        effective_date: this.sharedFormulaService.formatDateyyymmdd(Date.now()),
        type: quoteRequestType,
      };

      const quoteRequest$ = this.apiQuoteScheduleService.createNewQuoteRequest(quote_create_payload).pipe(take(1));
      let quoteReq: IQuoteRequest = (await firstValueFrom(quoteRequest$)).results;

      await this.reCreateQuoteRequestIfIncompleteDetailsBecauseOfFaultyBackendSyncWithoutValidationShouldRecreateQuote(quoteReq);
      // Commenting this out for now, Francois and I cant see why a new quoteRequest should be created as we are updating the vehciles. But this is going to be tested and will notify us if something major breaks. Then we can re add this.
      // if (shouldRecreate) {
      //   const quoteRequest$ = this.apiQuoteScheduleService.createNewQuoteRequest(quote_create_payload).pipe(take(1));
      //   quoteReq = (await firstValueFrom(quoteRequest$)).results;
      // }
      const completeQuoteRequest = await this.getQuoteRequestById(SharedConfig.user.id, quoteReq.id);
      this.sharedConfig.setQuoteRequest(completeQuoteRequest);
      this.sharedConfigService.setQuoteRequest(completeQuoteRequest);
      localStorage.setItem('hasSeenErrorPopup', 'false');
      if (showHideLoader) {
        this.sharedConfig.setShowCustomLoader(false, 'createNewQuoteRequest');
      }
      return quoteReq;
    } catch (error) {
      console.error('FAILED TO CREATE NEW QUOTE REQUEST');
      console.error(error);
      this.sharedConfig.setShowCustomLoader(false, 'createNewQuoteRequest');
      return null; // TODO Error handling generic class
    }
  }

  async createNewEndorsementRequest(
    quoteRequestType = QuoteRequestType.EndorsementAdd,
    schedule_id?: number,
    assetToUpdate?: IEndorsementUpdateAsset
  ): Promise<IQuoteRequest> {
    try {
      var quote_create_payload = {
        effective_date: this.sharedFormulaService.formatDateyyymmdd(Date.now()),
        type: quoteRequestType,
        // TODO WITH NEW FUNCTIONALITY TO SELECT MULTIPLE SCHEDULES
        schedule_id: schedule_id ? schedule_id : this.sharedConfig.schedulesActive[0].id,
      };

      if (assetToUpdate) {
        quote_create_payload['assets_to_update'] = assetToUpdate;
      }

      console.debug(quote_create_payload);

      const quoteRequest$ = this.apiQuoteScheduleService.createNewQuoteRequest(quote_create_payload).pipe(take(1));
      let quoteReq: IQuoteRequest = (await firstValueFrom(quoteRequest$)).results;

      const shouldRecreate =
        await this.reCreateQuoteRequestIfIncompleteDetailsBecauseOfFaultyBackendSyncWithoutValidationShouldRecreateQuote(quoteReq);
      // Commenting this out for now, Francois and I cant see why a new quoteRequest should be created as we are updating the vehciles. But this is going to be tested and will notify us if something major breaks. Then we can re add this.
      // if (shouldRecreate) {
      //   const quoteRequest$ = this.apiQuoteScheduleService.createNewQuoteRequest(quote_create_payload).pipe(take(1));
      //   quoteReq = (await firstValueFrom(quoteRequest$)).results;
      // }

      SharedConfig.quoteRequest = quoteReq;
      this.sharedConfigService.setQuoteRequest(quoteReq);
      localStorage.setItem('hasSeenErrorPopup', 'false');
      return quoteReq;
    } catch (error) {
      console.error('FAILED TO CREATE NEW QUOTE REQUEST');
      console.error(error);
      return null; // TODO Error handling generic class
    }
  }

  async reCreateQuoteRequestIfIncompleteDetailsBecauseOfFaultyBackendSyncWithoutValidationShouldRecreateQuote(
    quoteRequest: IQuoteRequest
  ): Promise<Boolean> {
    //----------------- OLD APP VEHICLE CHECK (MODELSERIES)
    let serverError = false;
    let reCreateQuoteRequest = false;
    let updateAsset = false;
    if (quoteRequest.assets?.vehicles) {
      for (const vehicle of quoteRequest.assets?.vehicles) {
        updateAsset = false;
        if (vehicle.year_of_manufacture && vehicle.model_id) {
          if (!vehicle.model_series) {
            reCreateQuoteRequest = true;
            updateAsset = true;
            const responseMotorVehicleResponse = (
              await firstValueFrom(this.apiAssetService.getVehicleModelByModelID(vehicle.year_of_manufacture.toString(), vehicle.model_id))
            ).results;
            const responseMotorVehicle = responseMotorVehicleResponse as IResponseMotorVehiclesSnakeCase;
            if (!responseMotorVehicle) {
              delete vehicle.model;
              delete vehicle.model_id;
              delete vehicle.model_series;
              delete vehicle.sum_insured;
              break;
            }

            vehicle.model_series = responseMotorVehicle.model.name;
          }
          if (vehicle.model_variant) {
            vehicle.model = vehicle.model_variant;
          }
          if (!vehicle.sum_insured) {
            reCreateQuoteRequest = true;
            updateAsset = true;
            let responseMotorVehicle;
            if (!responseMotorVehicle) {
              const responseMotorVehicleResponse = (
                await firstValueFrom(this.apiAssetService.getVehicleModelByModelID(vehicle.year_of_manufacture.toString(), vehicle.model_id))
              ).results;
              responseMotorVehicle = responseMotorVehicleResponse as IResponseMotorVehiclesSnakeCase;
            }

            if (
              (!responseMotorVehicle || !responseMotorVehicle.value_retail || responseMotorVehicle.value_retail <= 0) &&
              responseMotorVehicle.value_new <= 0
            ) {
              const alert = await this.alertController.create({
                header: 'Something went wrong',
                message: 'The sum insured value for your new vehicle could not be found. Please contact us.',
                buttons: ['OK'],
              });
              await alert.present();
            } else {
              if (responseMotorVehicle.value_retail > 0) {
                vehicle.sum_insured = responseMotorVehicle.value_retail / 100;
                vehicle.sum_insured_type = 'Retail value';
              } else {
                vehicle.sum_insured = responseMotorVehicle.value_new / 100;
                vehicle.sum_insured_type = 'As if it was new';
              }
            }
          }
          if (updateAsset) await this.apiAssetService.updateVehicle(vehicle).toPromise();
        }
      }
    }
    if (quoteRequest.assets?.motorcycles)
      for (const vehicle of quoteRequest.assets.motorcycles) {
        updateAsset = false;
        if (vehicle.year_of_manufacture && vehicle.model_id) {
          if (!vehicle.model_series) {
            reCreateQuoteRequest = true;
            updateAsset = true;
            const responseMotorVehicleResponse = (
              await firstValueFrom(this.apiAssetService.getVehicleModelByModelID(vehicle.year_of_manufacture.toString(), vehicle.model_id))
            ).results;
            const responseMotorVehicle = responseMotorVehicleResponse as IResponseMotorVehiclesSnakeCase;
            if (!responseMotorVehicle) {
              delete vehicle.model;
              delete vehicle.model_id;
              delete vehicle.model_series;
              delete vehicle.sum_insured;
              break;
            }
            vehicle.model_series = responseMotorVehicle.model.name;
          }
          if (vehicle.model_variant) {
            vehicle.model = vehicle.model_variant;
          }
          if (!vehicle.sum_insured) {
            reCreateQuoteRequest = true;
            updateAsset = true;
            const responseMotorVehicleResponse = (
              await firstValueFrom(this.apiAssetService.getVehicleModelByModelID(vehicle.year_of_manufacture.toString(), vehicle.model_id))
            ).results;
            const responseMotorVehicle = responseMotorVehicleResponse as IResponseMotorVehiclesSnakeCase;
            if (!responseMotorVehicle) {
              serverError = true;
              return;
            }
            if (responseMotorVehicle.value_retail > 0) {
              vehicle.sum_insured = responseMotorVehicle.value_retail / 100;
              vehicle.sum_insured_type = 'Retail value';
            } else {
              vehicle.sum_insured = responseMotorVehicle.value_new / 100;
              vehicle.sum_insured_type = 'As if it was new';
            }
          }
        }
        if (updateAsset) (await firstValueFrom(this.apiAssetService.updateMotorcycle(vehicle))).results;
      }
    if (serverError) {
      const failedAlert = await this.alertController.create({
        header: 'Something went wrong',
        subHeader: 'Please try again later or contact us',
        buttons: [
          {
            text: 'Ok',
            handler: () => {
              failedAlert.dismiss();
            },
          },
        ],
      });
      await failedAlert.present();
      return;
    }

    if (reCreateQuoteRequest) {
      return true;
    }

    return false;
  }

  async cancelQuoteRequest(): Promise<void> {
    const quote_create_payload: IQuoteRequest = {
      status: QuoteRequestStatusType.Cancelled,
      id: this.sharedConfig.quoteRequest.id,
    };

    this.sharedConfig.quoteRequest.status === QuoteRequestStatusType.Cancelled;
    await firstValueFrom(this.apiQuoteScheduleService.patchQuoteRequest(quote_create_payload));
  }

  async updateQuoteRequest(quoteRequest: IQuoteRequest): Promise<IQuoteRequest> {
    if (!quoteRequest) {
      await this.createNewQuoteRequest();
    }
    const setQuoteRequestResult = await firstValueFrom(this.apiQuoteScheduleService.setQuoteRequest(quoteRequest));
    return setQuoteRequestResult.results;
  }

  async getSpecialConditions(): Promise<ISpecialConditionAssets> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getSpecialConditions())).results;
  }

  async updateCoverStartDate(date: string, quote_request: IQuoteRequest): Promise<void> {
    (await firstValueFrom(this.apiQuoteScheduleService.updateCoverStartDate(date, quote_request))).results;
  }

  async getQuote(user_id: string): Promise<IQuote> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getQuote(user_id))).results;
  }

  async getBanks(): Promise<IBank[]> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getBanks())).results;
  }

  async getBankBranches(bankName: string): Promise<IBankBranches> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getBankBranches(bankName))).results;
  }

  async getPremiaBanks(): Promise<IPremiaLOV[]> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getPremiaBanks())).results || [];
  }

  async getPremiaBranches(bank: string): Promise<IPremiaLOV[]> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getPremiaBranches(bank))).results || [];
  }

  async getPremiaAccountTypes(): Promise<IPremiaLOV[]> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getPremiaAccountTypes())).results || [];
  }

  async getPremiaRelationshipTypes(): Promise<IPremiaLOV[]> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getPremiaRelationshipTypes())).results || [];
  }

  async getCasaList(): Promise<ICasaDetails> {
    const casaResponse = await firstValueFrom(this.apiQuoteScheduleService.loadCASAList());
    return casaResponse.results;
  }

  updateUserCasa(
    corCode,
    corDesc,
    cobCode,
    cobDesc,
    nationalityCode,
    nationalityDesc,
    sourceOfIncomeCode,
    sourceOfIncomeDesc,
    empCode,
    empDesc,
    occupation_code,
    occupationDesc
  ): void {
    this.apiQuoteScheduleService.updateCASA(
      corCode,
      corDesc,
      cobCode,
      cobDesc,
      nationalityCode,
      nationalityDesc,
      sourceOfIncomeCode,
      sourceOfIncomeDesc,
      empCode,
      empDesc,
      occupation_code,
      occupationDesc
    );
  }

  async kingPriceConfirm(quoteRequestID: number): Promise<IResponse> {
    return (await firstValueFrom(this.apiQuoteScheduleService.submitKingPriceConfirmation(quoteRequestID))).results;
  }

  async convertMiway(quoteRequestID: number): Promise<IAPIQuoteConvertResponse> {
    return (await firstValueFrom(this.apiQuoteScheduleService.miwayConvert(quoteRequestID))).results;
  }

  async getInsurers(): Promise<Array<IInsurersDetails>> {
    return (await firstValueFrom(this.apiQuoteScheduleService.getInsurers())).results;
  }

  async quoteConvert(quoteRequestID: number, quote_nr: string, bankingDetails: IBankingDetails): Promise<IQuoteConvertResponse> {
    return (await firstValueFrom(this.apiQuoteScheduleService.brolinkConvert(quoteRequestID, quote_nr, bankingDetails))).results;
  }

  async premiaBankingDetails(bankingDetails: IPremiaBankingDetails) {
    return await firstValueFrom(this.apiQuoteScheduleService.premiaBankingDetails(bankingDetails));
  }

  async convertPremiaQuote(quoteRequestID: number) {
    return (await firstValueFrom(this.apiQuoteScheduleService.premiaConvert(quoteRequestID)));
  }

  async convertSantamDirect(quoteRequestID: number): Promise<IQuoteConvertResponse> {
    return (await firstValueFrom(this.apiQuoteScheduleService.santamDirectConvert(quoteRequestID))).results;
  }

  async acceptEndorsement(quoteRequestID: number): Promise<ICtrlResponse<String>> {
    const response = await firstValueFrom(this.apiQuoteScheduleService.acceptEndorsement(quoteRequestID));
    return response;
  }

  async declineEndorsement(quoteRequestID: number): Promise<ICtrlResponse<String>> {
    const response = await firstValueFrom(this.apiQuoteScheduleService.declineEndorsement(quoteRequestID));
    return response;
  }
}
